// bookings/hooks/useMotorcycleLoader.js
import { useEffect, useRef, useState } from "react";

export function useMotorcycleLoader({
    shopMotorcycleId,
    isHydrating,
    setValues,
    setShopMotorcycleExtras,
    fetchShopMotorcycle, // 约定：返回 data 本体
}) {
    const [isLoading, setIsLoading] = useState(false);
    const reqIdRef = useRef(0);
    const lastLoadedIdRef = useRef("");

    useEffect(() => {
        if (isHydrating) return;

        const normalizedId = String(shopMotorcycleId ?? "");
        if (!normalizedId) {
            lastLoadedIdRef.current = "";
            setShopMotorcycleExtras([]);
            setIsLoading(false);
            return;
        }

        if (lastLoadedIdRef.current === normalizedId) return;
        lastLoadedIdRef.current = normalizedId;

        let cancelled = false;
        const reqId = ++reqIdRef.current;

        setIsLoading(true);
        setShopMotorcycleExtras([]);

        (async () => {
            try {
                const data = await fetchShopMotorcycle(normalizedId); // ✅ data 本体
                if (cancelled) return;
                if (reqId !== reqIdRef.current) return;

                const extrasFromApi = data?.ShopMotorcycleExtra ?? [];

                setValues((prev) => {
                    const next = {
                        ...prev,
                        motorcycleSnapshot: {
                            ...(prev?.motorcycleSnapshot ?? {}),
                            shopMotorcycle: {
                                ...(prev?.motorcycleSnapshot?.shopMotorcycle ?? {}),
                                motorcycleYear: data?.motorcycleYear ?? "",
                                motorcycleModel: {
                                    ...(prev?.motorcycleSnapshot?.shopMotorcycle?.motorcycleModel ?? {}),
                                    id: data?.MotorcycleModel?.id ?? "",
                                    name: data?.MotorcycleModel?.name ?? "",
                                    brand: data?.MotorcycleModel?.Brand?.name ?? "",
                                    type: data?.MotorcycleModel?.type ?? "",
                                    photoUrl: data?.MotorcycleModel?.Media?.url ?? "",
                                    modelYear: data?.MotorcycleModel?.modelYear ?? "",
                                },
                            },
                        },
                        rentalPolicySnapshot: {
                            ...(prev?.rentalPolicySnapshot ?? {}),
                            minAge: data?.RentalPolicy?.minAge ?? "",
                            minExp: data?.RentalPolicy?.minExp ?? "",
                            countryRestriction: data?.RentalPolicy?.countryRestriction ?? false,
                            canLeaveState: data?.RentalPolicy?.canLeaveState ?? false,
                            canLeaveCity: data?.RentalPolicy?.canLeaveCity ?? false,
                            isUnlimitedMileage: data?.RentalPolicy?.isUnlimitedMileage ?? true,
                            mileageLimit: data?.RentalPolicy?.mileageLimit ?? "",
                            excessMileageFee: data?.RentalPolicy?.excessMileageFee ?? "",
                            deposit: data?.RentalPolicy?.deposit ?? "",
                            damageExcess: data?.RentalPolicy?.damageExcess ?? "",
                            theftExcess: data?.RentalPolicy?.theftExcess ?? "",
                            licenseType: data?.RentalPolicy?.licenseType ?? "",
                        },
                    };

                    // ✅ 对齐 extras
                    const prevOrderExtras = prev?.rentalOrderExtra ?? [];
                    const extrasMap = new Map(extrasFromApi.map((e) => [e.id, e]));

                    next.rentalOrderExtra = prevOrderExtras
                        .filter((item) => extrasMap.has(item.shopMotorcycleExtraId))
                        .map((item) => {
                            const extra = extrasMap.get(item.shopMotorcycleExtraId);
                            return {
                                ...item,
                                name: extra?.name ?? item.name,
                                description: extra?.description ?? item.description,
                            };
                        });

                    return next;
                });

                setShopMotorcycleExtras(extrasFromApi);
            } finally {
                if (!cancelled && reqId === reqIdRef.current) setIsLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [shopMotorcycleId, isHydrating, setValues, setShopMotorcycleExtras, fetchShopMotorcycle]);

    return isLoading;
}